// Справочники-перечисления, общие для нескольких карточек.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ. Стадия сделки нужна и самой сделке (поле), и её показателю в шапке, и
// подсказке в поиске связей; ставка НДС — сделке и счёту; вид проверки — только проверке,
// но лежит рядом, чтобы искать справочники в одном месте. Без общего файла коды разъедутся:
// в поле будет одно, а в показателе другое, и «успешные сделки» перестанут считаться.
//
// ОТКУДА КОДЫ И ПОДПИСИ — И ПОЧЕМУ ИМЕННО ОТТУДА. Всё, что ниже, выгружено ЗАПРОСОМ к
// справочникам базы `cir_core` 29.07.2026, а не переписано из документа. Причина названа
// прямо: справочники наполнены переносом из настоящих данных Bitrix, на них смотрят внешние
// ключи, и перенос уже записал этими кодами 15 895 сделок. Список в описании карточки на них
// не влияет никак — он только решает, что человеку показать и что считать опечаткой.
// Разошлись они до 29.07.2026 настолько, что завести сделку было нельзя вовсе: описание
// предлагало `C4:WON`, `work`, `vat20`, `client`, а в справочниках лежат `won`, `navigaciya`,
// `nds20`, `customer` — ни одного общего кода. Правились описания, справочники не трогались.
//
// ЧТО СЛОМАЕТСЯ, ЕСЛИ ДОПИСАТЬ СЮДА КОД ОТ СЕБЯ. Ничего не сломается на экране — и в этом
// беда: форма покажет вариант, человек его выберет, а слой записи ответит «кода нет в
// справочнике», потому что внешнему ключу писать не на что. Проверка стоит отдельно:
// `cards_write.сверить_справочники(pool)` печатает каждый код описания, которого нет в базе.
// Гонять её после любой правки этого файла.
//
// ПОЧЕМУ ПОДПИСИ НЕКРАСИВЫЕ. «Ремонт (НЕ ИСПОЛЬЗОВАТЬ)», «-», «Отдел координации и БТО №1» —
// это дословные названия из справочника. Причёсывать их здесь нельзя: тогда в CRM и в
// отчётах по зеркалу одно и то же значение будет называться по-разному, и сверить их станет
// нечем. Название правится у владельца справочника, а не в описании карточки.

import { пусто, строка } from '../values'
import type { Вариант, Значения } from '../types'

// ─────────────────────────── воронки и стадии сделки ───────────────────────────

/**
 * Воронки. Восемь живых, все из справочника `pipeline`.
 *
 * Воронка обязательна не для порядка: код стадии уникален ВНУТРИ воронки, а не вообще
 * (см. `СТАДИИ_ВОРОНКИ` ниже). Без воронки строка `executing` не указывает ни на что.
 */
export const ВОРОНКИ: Вариант[] = [
  { код: 'akademiya_sir', подпись: 'Академия СИР' },
  { код: 'avito', подпись: 'Авито' },
  { код: 'bitriks', подпись: 'Битрикс' },
  { код: 'kontraktnoe_proizvodstvo', подпись: 'Контрактное производство' },
  { код: 'navigaciya', подпись: 'НАВИГАЦИЯ' },
  { код: 'obschaya', подпись: 'Общая' },
  { код: 'sir', подпись: 'СиР' },
  { код: 'snabzhenie', подпись: 'Снабжение' },
]

/**
 * Стадии, разложенные по воронкам, — главный список, а не вспомогательный.
 *
 * ГЛАВНАЯ ЛОВУШКА ЭТОГО ФАЙЛА. В `stage` 92 строки, но разных кодов среди них 38: код
 * уникален внутри воронки. `executing` — это шесть РАЗНЫХ стадий («В работе» в Битриксе,
 * «Отправка счета» в пяти прочих), `apology` — три, `won` — восемь, и подписи у них тоже
 * разные («Успех», «Сделка успешна», «Сделка заключена», «Счет отправлен, работы
 * выполнены»). Поэтому поле стадии в карточке сужается по выбранной воронке
 * (`Поле.вариантыПо`), а не показывает все 38 подряд: иначе человек выберет чужую стадию,
 * а слой записи молча найдёт по коду ПЕРВУЮ попавшуюся строку `stage` — возможно, из другой
 * воронки. Это не догадка: `cards_write._код_справочника` ищет `WHERE code = %s` без воронки.
 */
export const СТАДИИ_ВОРОНКИ: Record<string, Вариант[]> = {
  akademiya_sir: [
    { код: 'new', подпись: 'Новая заявка' },
    { код: 'preparation', подпись: 'Презентация' },
    { код: 'prepayment_invoice', подпись: 'Заключение договора' },
    { код: 'executing', подпись: 'Отправка счета' },
    { код: 'final_invoice', подпись: 'Идет обучение' },
    { код: 'uc_v6hinf', подпись: 'Закрывающие документы' },
    { код: 'uc_99rjue', подпись: 'Выдача сертификатов/дипломов' },
    { код: 'uc_rykvzj', подпись: 'Обратная связь' },
    { код: 'won', подпись: 'Успех' },
    { код: 'lose', подпись: 'Сделка провалена' },
  ],
  avito: [
    { код: 'new', подпись: 'Запрос получен' },
    { код: 'preparation', подпись: 'Отправка КП' },
    { код: 'prepayment_invoice', подпись: 'Подтверждение заявки' },
    { код: 'executing', подпись: 'Отправка счета' },
    { код: 'final_invoice', подпись: 'Отгрузка товара' },
    { код: 'uc_vxuj0l', подпись: 'Отчет' },
    { код: 'uc_z2pbf7', подпись: 'Ждем оплату' },
    { код: 'won', подпись: 'Сделка успешна' },
    { код: 'lose', подпись: 'Сделка провалена' },
    { код: 'apology', подпись: 'Анализ причины провала' },
  ],
  bitriks: [
    { код: 'new', подпись: 'Новая' },
    { код: 'executing', подпись: 'В работе' },
    { код: 'preparation', подпись: 'Подготовка предложения' },
    { код: 's2', подпись: 'Договор' },
    { код: 'prepayment_invoice', подпись: 'Cчёт на предоплату' },
    { код: 'uc_1bvlln', подпись: 'Реализация' },
    { код: 'final_invoice', подпись: 'Финальный счёт' },
    { код: 's1', подпись: 'Отгрузка документов' },
    { код: 'won', подпись: 'Сделка успешна' },
    { код: 'lose', подпись: 'Сделка провалена' },
    { код: 'apology', подпись: 'Анализ причины провала' },
  ],
  kontraktnoe_proizvodstvo: [
    { код: 'new', подпись: 'Запрос получен' },
    { код: 'preparation', подпись: 'Отправка КП' },
    { код: 'prepayment_invoic', подпись: 'Подтверждение заявки/Договор' },
    { код: 'executing', подпись: 'Отправка счета' },
    { код: 'final_invoice', подпись: 'Оплата получена' },
    { код: 'uc_x70tk9', подпись: 'Закупка компонентов' },
    { код: 'uc_hbtk1r', подпись: 'Ожидание компонентов' },
    { код: 'uc_1lpi9w', подпись: 'Работы выполняются' },
    { код: 'uc_j8312i', подпись: 'Отгрузка' },
    { код: 'won', подпись: 'Успех' },
    { код: 'lose', подпись: 'Сделка провалена' },
  ],
  navigaciya: [
    { код: 'new', подпись: 'Сбор данных' },
    { код: 'proposal', подпись: 'Предложение' },
    { код: 's1', подпись: 'ЖДУН' },
    { код: 'negotiation', подпись: 'Отправлен счет' },
    { код: 's6', подпись: 'Отгрузка товара' },
    { код: 's7', подпись: 'Отчет' },
    { код: 'won', подпись: 'Сделка заключена' },
    { код: 'lose', подпись: 'Отменена нами' },
    { код: 's3', подпись: 'Отменена заказчиком' },
  ],
  obschaya: [
    { код: 'new', подпись: 'Запрос получен' },
    { код: 's6', подпись: 'Отправка КП' },
    { код: 's7', подпись: 'Подтверждение заявки / договор' },
    { код: 'executing', подпись: 'Отправка счета' },
    { код: 's8', подпись: 'Ждем судно / товар' },
    { код: 'preparation', подпись: 'Выбор исполнителя, заказ пропусков' },
    { код: 'final_invoice', подпись: 'Отгрузка товара' },
    { код: 'prepayment_invoice', подпись: 'Работы выполняются' },
    { код: 'uc_9kdmwi', подпись: 'Работы завершены' },
    { код: 'uc_lj40sq', подпись: 'Проблемная сделка' },
    { код: 's2', подпись: 'Выставление АВР' },
    { код: 'uc_ju33pd', подпись: 'Исправление отчета' },
    { код: 's3', подпись: 'Отчет' },
    { код: 's9', подпись: 'Ждем оплату' },
    { код: 'won', подпись: 'Сделка успешна' },
    { код: 'lose', подпись: 'Отменена заказчиком' },
    { код: 's5', подпись: 'Отменена нами' },
    { код: 's4', подпись: 'Гарантия / не выставляется' },
    { код: 's10', подпись: 'На будущий год' },
  ],
  sir: [
    { код: 'new', подпись: 'В обработке' },
    { код: 's4', подпись: 'Согласование стоимости, получение данных' },
    { код: 's3', подпись: 'согласование товаров' },
    { код: 'details', подпись: 'Выбор исполнителя, оформление пропусков' },
    { код: 's2', подпись: 'Ожидание' },
    { код: 's7', подпись: 'РЕМОНТ' },
    { код: 's5', подпись: 'ПРОБЛЕМА НЕ ЗАКРЫТА!' },
    { код: 'proposal', подпись: 'Отправка АВР, подготовка для передачи в бухгалтерию' },
    { код: 's1', подпись: 'Выставление счета, отправка опросника' },
    { код: 'won', подпись: 'Счет отправлен, работы выполнены' },
    { код: 'lose', подпись: 'Заявка отменена' },
    { код: 'on_hold', подпись: 'Гарантия/не выставляется' },
  ],
  snabzhenie: [
    { код: 'new', подпись: 'Запрос получен' },
    { код: 'preparation', подпись: 'Отправка КП' },
    { код: 'prepayment_invoic', подпись: 'Подтверждение / Договор' },
    { код: 'final_invoice', подпись: 'Отгрузка' },
    { код: 'executing', подпись: 'Отправка счета' },
    { код: 'uc_jwwfso', подпись: 'Отчет' },
    { код: 'uc_vnikjg', подпись: 'Ждем оплату' },
    { код: 'won', подпись: 'Сделка успешна' },
    { код: 'lose', подпись: 'Отменена нами' },
    { код: 'apology', подпись: 'Анализ причины провала' },
  ],
}

/**
 * Все стадии одним списком — объединение кодов из `СТАДИИ_ВОРОНКИ`, 38 штук.
 *
 * ЗАЧЕМ ОН НУЖЕН, ЕСЛИ ЕСТЬ РАЗБИЕНИЕ ПО ВОРОНКАМ. Слой записи и `сверить_справочники`
 * спрашивают у поля один плоский набор кодов: им важно, есть ли код в справочнике базы,
 * а воронку они не знают. Он же остаётся запасным для показа, когда воронка ещё не выбрана.
 *
 * ПОЧЕМУ ПОДПИСИ ЧЕРЕЗ КОСУЮ ЧЕРТУ. У кода, встречающегося в нескольких воронках, ОДНОЙ
 * подписи нет — их несколько, и все настоящие. Выбрать одну значило бы соврать про остальные
 * воронки, поэтому перечислены все. В выпадающем списке этого не видно: там показывается
 * набор своей воронки, где подпись ровно одна.
 */
export const СТАДИИ: Вариант[] = [
  { код: 'new', подпись: 'В обработке / Запрос получен / Новая / Новая заявка / Сбор данных' },
  { код: 'preparation', подпись: 'Выбор исполнителя, заказ пропусков / Отправка КП / Подготовка предложения / Презентация' },
  { код: 'prepayment_invoice', подпись: 'Cчёт на предоплату / Заключение договора / Подтверждение заявки / Работы выполняются' },
  { код: 'executing', подпись: 'В работе / Отправка счета' },
  { код: 'final_invoice', подпись: 'Идет обучение / Оплата получена / Отгрузка / Отгрузка товара / Финальный счёт' },
  { код: 'uc_v6hinf', подпись: 'Закрывающие документы' },
  { код: 'uc_99rjue', подпись: 'Выдача сертификатов/дипломов' },
  { код: 'uc_rykvzj', подпись: 'Обратная связь' },
  { код: 'won', подпись: 'Сделка заключена / Сделка успешна / Счет отправлен, работы выполнены / Успех' },
  { код: 'lose', подпись: 'Заявка отменена / Отменена заказчиком / Отменена нами / Сделка провалена' },
  { код: 'uc_vxuj0l', подпись: 'Отчет' },
  { код: 'uc_z2pbf7', подпись: 'Ждем оплату' },
  { код: 'apology', подпись: 'Анализ причины провала' },
  { код: 's2', подпись: 'Выставление АВР / Договор / Ожидание' },
  { код: 'uc_1bvlln', подпись: 'Реализация' },
  { код: 's1', подпись: 'Выставление счета, отправка опросника / ЖДУН / Отгрузка документов' },
  { код: 'prepayment_invoic', подпись: 'Подтверждение / Договор / Подтверждение заявки/Договор' },
  { код: 'uc_x70tk9', подпись: 'Закупка компонентов' },
  { код: 'uc_hbtk1r', подпись: 'Ожидание компонентов' },
  { код: 'uc_1lpi9w', подпись: 'Работы выполняются' },
  { код: 'uc_j8312i', подпись: 'Отгрузка' },
  { код: 'proposal', подпись: 'Отправка АВР, подготовка для передачи в бухгалтерию / Предложение' },
  { код: 'negotiation', подпись: 'Отправлен счет' },
  { код: 's6', подпись: 'Отгрузка товара / Отправка КП' },
  { код: 's7', подпись: 'Отчет / Подтверждение заявки / договор / РЕМОНТ' },
  { код: 's3', подпись: 'Отменена заказчиком / Отчет / согласование товаров' },
  { код: 's8', подпись: 'Ждем судно / товар' },
  { код: 'uc_9kdmwi', подпись: 'Работы завершены' },
  { код: 'uc_lj40sq', подпись: 'Проблемная сделка' },
  { код: 'uc_ju33pd', подпись: 'Исправление отчета' },
  { код: 's9', подпись: 'Ждем оплату' },
  { код: 's5', подпись: 'Отменена нами / ПРОБЛЕМА НЕ ЗАКРЫТА!' },
  { код: 's4', подпись: 'Гарантия / не выставляется / Согласование стоимости, получение данных' },
  { код: 's10', подпись: 'На будущий год' },
  { код: 'details', подпись: 'Выбор исполнителя, оформление пропусков' },
  { код: 'on_hold', подпись: 'Гарантия/не выставляется' },
  { код: 'uc_jwwfso', подпись: 'Отчет' },
  { код: 'uc_vnikjg', подпись: 'Ждем оплату' },
]

/** Успех. В справочнике `kind = 'won'` стоит ровно у одного кода — и он одинаков во всех восьми воронках. */
export const СТАДИЯ_УСПЕХА = 'won'

/**
 * Провальные стадии по воронкам: `kind = 'lost'` из справочника.
 *
 * По воронкам, а не одним списком, ровно по той же причине, что и сами стадии: `s3` — это
 * «Отменена заказчиком» в НАВИГАЦИИ и «Отчет» в Общей, `s4` — «Гарантия / не выставляется» в
 * Общей и «Согласование стоимости» в СиР, `s5` — «Отменена нами» в Общей и «ПРОБЛЕМА НЕ
 * ЗАКРЫТА!» в СиР. Спросить «проиграна ли сделка» по одному коду нельзя.
 */
export const СТАДИИ_ПРОИГРЫША_ВОРОНКИ: Record<string, string[]> = {
  akademiya_sir: ['lose'],
  avito: ['lose', 'apology'],
  bitriks: ['lose', 'apology'],
  kontraktnoe_proizvodstvo: ['lose'],
  navigaciya: ['lose', 's3'],
  obschaya: ['lose', 's5', 's4', 's10'],
  sir: ['lose', 'on_hold'],
  snabzhenie: ['lose', 'apology'],
}

/**
 * Те же коды одним списком — запасной ответ, когда воронка неизвестна.
 *
 * Он ШИРЕ правды: `s3`, `s4`, `s5` попали сюда из-за одной воронки каждый, а в других
 * воронках это рабочие стадии. Пользоваться им поэтому только там, где воронки нет вовсе.
 */
export const СТАДИИ_ПРОИГРЫША: string[] = ['apology', 'lose', 'on_hold', 's10', 's3', 's4', 's5']

/**
 * Сделка проиграна. Отсюда обязательность причины проигрыша.
 *
 * Воронка берётся из тех же значений: без неё ответ был бы гаданием по коду.
 */
export const проиграна = (з: Значения): boolean => {
  const стадия = строка(з, 'stage_id')
  if (стадия === '') return false
  const воронка = строка(з, 'pipeline_id')
  const свои = СТАДИИ_ПРОИГРЫША_ВОРОНКИ[воронка]
  return свои ? свои.includes(стадия) : СТАДИИ_ПРОИГРЫША.includes(стадия)
}

/**
 * Подпись стадии для показателя в шапке.
 *
 * Воронка — второй довод, а не первый, нарочно: показатель зовут и там, где воронки под
 * рукой нет. Кода нет нигде — показываем сам код: прочерк соврал бы «не заполнено».
 */
export function подписьСтадии(код: string, воронка = ''): string {
  const свои = СТАДИИ_ВОРОНКИ[воронка]
  const своя = свои?.find((с) => с.код === код)?.подпись
  return своя ?? СТАДИИ.find((с) => с.код === код)?.подпись ?? (код || '—')
}

/**
 * Тип сделки — справочник `deal_type`.
 *
 * Половина списка помечена «НЕ ИСПОЛЬЗОВАТЬ» и `type` называется «-». Так в справочнике, и
 * так это выглядит у людей в Bitrix сегодня; вычистить его — работа владельца справочника.
 */
export const ТИПЫ_СДЕЛКИ: Вариант[] = [
  { код: 'ne_vybrano', подпись: 'Не выбрано' },
  { код: 'prodazha_s_ustanovkoy', подпись: 'Продажа с установкой' },
  { код: 'eng', подпись: 'Eng' },
  { код: 'tender', подпись: 'Тендер' },
  { код: 'type', подпись: '-' },
  { код: 'prodazha_tovara', подпись: 'Продажа товара' },
  { код: 'servis', подпись: 'Сервис' },
  { код: 'remont_ne_ispolzovat', подпись: 'Ремонт (НЕ ИСПОЛЬЗОВАТЬ)' },
  { код: 'szch_ne_ispolzovat', подпись: 'СЗЧ (НЕ ИСПОЛЬЗОВАТЬ)' },
  { код: 'kompleksnaya_os_ne_ispolzovat', подпись: 'Комплексная ОС (НЕ ИСПОЛЬЗОВАТЬ)' },
  { код: 'kompleksnaya_obschaya_ne_ispolzovat', подпись: 'Комплексная Общая (НЕ ИСПОЛЬЗОВАТЬ)' },
  { код: 'kompleksnaya_prodazha_ne_ispolzovat', подпись: 'Комплексная продажа (НЕ ИСПОЛЬЗОВАТЬ)' },
  { код: 'garantiya', подпись: 'Гарантия' },
  { код: 'yahty', подпись: 'Яхты' },
  { код: 'isu_ts', подпись: 'ИСУ-ТС' },
  { код: 'holodnaya_prodazha_os', подпись: 'Холодная продажа (ОС)' },
]

// ─────────────────────────── деньги ───────────────────────────

/**
 * Ставки НДС — справочник `vat_rate`.
 *
 * Ставка ссылкой, а не числом в поле: в одном году ставка поехала с двадцати процентов на
 * двадцать два, а рядом живут льготная и старая, и прошлогодний документ по сегодняшней
 * ставке пересчитывать нельзя.
 */
export const СТАВКИ_НДС: Вариант[] = [
  { код: 'nds18', подпись: 'НДС 18 %' },
  { код: 'nds20', подпись: 'НДС 20 %' },
  { код: 'nds22', подпись: 'НДС 22 %' },
  { код: 'nds5', подпись: 'НДС 5 %' },
  { код: 'nds0', подпись: 'НДС 0 %' },
  { код: 'no_vat', подпись: 'Без НДС' },
]

// ─────────────────────────── контрагенты ───────────────────────────

/**
 * Виды компаний — справочник `company_kind`.
 *
 * «Наша компания» и «Банк» отдельными видами тут НЕ заведены, хотя раньше описание их
 * предлагало. Своё юрлицо отличается не видом, а тем, что на него смотрит `own_company_id`;
 * банк живёт в реквизитах. Заводить их видом здесь значило бы завести код, которого в
 * справочнике нет, — то есть вариант, который нельзя записать.
 */
export const ВИДЫ_КОМПАНИЙ: Вариант[] = [
  { код: 'customer', подпись: 'Клиент' },
  { код: 'supplier', подпись: 'Поставщик' },
  { код: 'foreign', подпись: 'Иностранная компания' },
  { код: 'partner', подпись: 'Партнёр' },
  { код: 'competitor', подпись: 'Конкурент' },
  { код: 'inactive_customer', подпись: 'Неактуальный клиент' },
  { код: 'unspecified', подпись: 'Не указан' },
]

/**
 * Страны флага — справочник `country`, на него смотрит `vessel.flag_id`.
 *
 * Справочник, а не строка: флаг записан десятками написаний, и отбор по одному из них
 * недосчитывает суда под тем же флагом. «Неизвестно» тут нет — незнание выражается пустым
 * полем, а не значением справочника.
 */
export const СТРАНЫ_ФЛАГА: Вариант[] = [
  { код: 'AE', подпись: 'ОАЭ' },
  { код: 'AG', подпись: 'Антигуа и Барбуда' },
  { код: 'AW', подпись: 'Аруба' },
  { код: 'AZ', подпись: 'Азербайджан' },
  { код: 'BB', подпись: 'Барбадос' },
  { код: 'BG', подпись: 'Болгария' },
  { код: 'BS', подпись: 'Багамы' },
  { код: 'BZ', подпись: 'Белиз' },
  { код: 'CK', подпись: 'Острова Кука' },
  { код: 'CM', подпись: 'Камерун' },
  { код: 'CN', подпись: 'Китай' },
  { код: 'CY', подпись: 'Кипр' },
  { код: 'DE', подпись: 'Германия' },
  { код: 'DK', подпись: 'Дания' },
  { код: 'DM', подпись: 'Доминика' },
  { код: 'EE', подпись: 'Эстония' },
  { код: 'EG', подпись: 'Египет' },
  { код: 'FI', подпись: 'Финляндия' },
  { код: 'GB', подпись: 'Великобритания' },
  { код: 'GE', подпись: 'Грузия' },
  { код: 'GM', подпись: 'Гамбия' },
  { код: 'GN', подпись: 'Гвинея' },
  { код: 'GQ', подпись: 'Экваториальная Гвинея' },
  { код: 'GR', подпись: 'Греция' },
  { код: 'GW', подпись: 'Гвинея-Бисау' },
  { код: 'GY', подпись: 'Гайана' },
  { код: 'HK', подпись: 'Гонконг' },
  { код: 'IN', подпись: 'Индия' },
  { код: 'IR', подпись: 'Иран' },
  { код: 'IT', подпись: 'Италия' },
  { код: 'JO', подпись: 'Иордания' },
  { код: 'JP', подпись: 'Япония' },
  { код: 'KH', подпись: 'Камбоджа' },
  { код: 'KM', подпись: 'Коморские Острова' },
  { код: 'KN', подпись: 'Сент-Китс и Невис' },
  { код: 'KR', подпись: 'Республика Корея' },
  { код: 'KY', подпись: 'Каймановы Острова' },
  { код: 'KZ', подпись: 'Казахстан' },
  { код: 'LR', подпись: 'Либерия' },
  { код: 'LT', подпись: 'Литва' },
  { код: 'LV', подпись: 'Латвия' },
  { код: 'MD', подпись: 'Молдова' },
  { код: 'MH', подпись: 'Маршалловы Острова' },
  { код: 'MM', подпись: 'Мьянма' },
  { код: 'MN', подпись: 'Монголия' },
  { код: 'MT', подпись: 'Мальта' },
  { код: 'MZ', подпись: 'Мозамбик' },
  { код: 'NA', подпись: 'Намибия' },
  { код: 'NL', подпись: 'Нидерланды' },
  { код: 'NO', подпись: 'Норвегия' },
  { код: 'NR', подпись: 'Науру' },
  { код: 'NZ', подпись: 'Новая Зеландия' },
  { код: 'OM', подпись: 'Оман' },
  { код: 'PA', подпись: 'Панама' },
  { код: 'PL', подпись: 'Польша' },
  { код: 'PW', подпись: 'Палау' },
  { код: 'RO', подпись: 'Румыния' },
  { код: 'RU', подпись: 'Россия' },
  { код: 'SG', подпись: 'Сингапур' },
  { код: 'SL', подпись: 'Сьерра-Леоне' },
  { код: 'SM', подпись: 'Сан-Марино' },
  { код: 'ST', подпись: 'Сан-Томе и Принсипи' },
  { код: 'SX', подпись: 'Синт-Мартен' },
  { код: 'TG', подпись: 'Того' },
  { код: 'TL', подпись: 'Тимор-Лесте' },
  { код: 'TM', подпись: 'Туркменистан' },
  { код: 'TR', подпись: 'Турция' },
  { код: 'TV', подпись: 'Тувалу' },
  { код: 'TZ', подпись: 'Танзания' },
  { код: 'UA', подпись: 'Украина' },
  { код: 'VC', подпись: 'Сент-Винсент и Гренадины' },
  { код: 'VN', подпись: 'Вьетнам' },
  { код: 'VU', подпись: 'Вануату' },
  { код: 'ZM', подпись: 'Замбия' },
]

/** Типы судов — справочник `vessel_type`. */
export const ТИПЫ_СУДОВ: Вариант[] = [
  { код: 'tanker', подпись: 'Нефтеналивное (танкеры, химовозы, газовозы, бункеровщики)' },
  { код: 'general_cargo', подпись: 'Суда для генерального груза' },
  { код: 'fishing', подпись: 'Рыболовное судно' },
  { код: 'tug', подпись: 'Буксир' },
  { код: 'bulk_carrier', подпись: 'Навалочное (балкеры, рудовозы, нефтерудовозы)' },
  { код: 'reefer', подпись: 'Рефрижераторное судно' },
  { код: 'passenger', подпись: 'Пассажирское судно' },
  { код: 'container', подпись: 'Контейнеровоз, баржевоз, доковое судно' },
  { код: 'offshore_supply', подпись: 'Судно обеспечения' },
  { код: 'research', подпись: 'Научно-исследовательское судно' },
  { код: 'ro_pax', подпись: 'Грузопассажирское судно, паром' },
  { код: 'icebreaker', подпись: 'Ледокол' },
  { код: 'dredger', подпись: 'Земснаряд' },
  { код: 'other', подпись: 'Прочие суда' },
]

// ─────────────────────────── свои подразделения и роли ───────────────────────────

/**
 * Подразделения — справочник `org_unit`, сто одна живая запись.
 *
 * Списком, а не связью: карточки подразделения в конструкторе нет. Список длинный, и это
 * не недосмотр — столько подразделений в компании и есть; сокращать его здесь значило бы
 * не дать выбрать своё тем, кого сократили.
 */
export const ПОДРАЗДЕЛЕНИЯ: Вариант[] = [
  { код: 'buhgalteriya_sir', подпись: 'Бухгалтерия СиР' },
  { код: 'buhgalteriya_sir_ptz', подпись: 'Бухгалтерия СиР ПТЗ' },
  { код: 'finansovyy_otdel', подпись: 'Финансовый отдел' },
  { код: 'koordinaciya_avtomatika_rnd', подпись: 'Координация автоматика РнД' },
  { код: 'koordinaciya_murmansk', подпись: 'Координация Мурманск' },
  { код: 'koordinaciya_rnd', подпись: 'Координация РнД' },
  { код: 'otdel_koordinacii_i_bto_1', подпись: 'Отдел координации и БТО №1' },
  { код: 'tendernyy_otdel', подпись: 'Тендерный отдел' },
  { код: 'spb', подпись: 'СПБ' },
  { код: 'otdel_koordinacii_i_bto_2', подпись: 'Отдел координации и БТО №2' },
  { код: 'otdel_servisa_rnd', подпись: 'Отдел сервиса РнД' },
  { код: 'sir_tehnologii', подпись: 'СИР Технологии' },
  { код: 'finansy', подпись: 'Финансы' },
  { код: 'murmansk_avtomatika_i_elektrika', подпись: 'Мурманск Автоматика И Электрика' },
  { код: 'otdel_avtomatiki_i_elektriki_rnd', подпись: 'Отдел автоматики и электрики РнД' },
  { код: 'tehnicheskaya_podderzhka', подпись: 'Техническая поддержка' },
  { код: 'proizvodstvennyy_otdel', подпись: 'Производственный отдел' },
  { код: 'sklad', подпись: 'Склад' },
  { код: 'sir_akademiya', подпись: 'СИР Академия' },
  { код: 'finansovye_kontrolery', подпись: 'Финансовые контролеры' },
  { код: 'op_1', подпись: 'ОП №1' },
  { код: 'op_murmansk', подпись: 'ОП Мурманск' },
  { код: 'otdel_remonta_rnd', подпись: 'Отдел ремонта РнД' },
  { код: 'otdel_servisa_spb', подпись: 'Отдел сервиса СПб' },
  { код: 'servis_avtomatika_rnd', подпись: 'Сервис автоматика РнД' },
  { код: 'otdel_servisa_novorossiysk', подпись: 'Отдел сервиса Новороссийск' },
  { код: 'montazhnaya_gruppa', подпись: 'Монтажная группа' },
  { код: 'ofis_menedzhery', подпись: 'Офис-менеджеры' },
  { код: 'otdel_servisa_astrahan', подпись: 'Отдел сервиса Астрахань' },
  { код: 'op_2', подпись: 'ОП №2' },
  { код: 'otdel_remonta_murmansk', подпись: 'Отдел ремонта Мурманск' },
  { код: 'sputnikovoe_oborudovanie', подпись: 'Спутниковое оборудование' },
  { код: 'otdel_proizvodstva_metallokonstrukciy', подпись: 'Отдел производства металлоконструкций' },
  { код: 'otdel_personala', подпись: 'Отдел персонала' },
  { код: 'polnomochnoe_predstavitelstvo_1', подпись: 'Полномочное представительство 1' },
  { код: 'teh_podderzhka_rnd', подпись: 'Тех поддержка РнД' },
  { код: 'arhangelsk', подпись: 'Архангельск' },
  { код: 'avtomatika_dv', подпись: 'Автоматика ДВ' },
  { код: 'buhgalteriya', подпись: 'Бухгалтерия' },
  { код: 'buhgalteriya_dv', подпись: 'Бухгалтерия ДВ' },
  { код: 'buhgalteriya_rnd', подпись: 'Бухгалтерия РнД' },
  { код: 'gruppa_teh_podderzhki', подпись: 'Группа тех. поддержки' },
  { код: 'koordinaciya_astrahan', подпись: 'Координация Астрахань' },
  { код: 'koordinaciya_novorossiysk', подпись: 'Координация Новороссийск' },
  { код: 'op_sputnikovoy_svyazi', подпись: 'ОП Спутниковой связи' },
  { код: 'otdel_koordinacii_avtomatiki_spb', подпись: 'Отдел координации автоматики СПб' },
  { код: 'otdel_koordinacii_dv', подпись: 'Отдел Координации ДВ' },
  { код: 'otdel_prodazh_b24', подпись: 'Отдел продаж Б24' },
  { код: 'otdel_prodazh_i_zakupok', подпись: 'Отдел продаж и закупок' },
  { код: 'otdel_razrabotki_tehnicheskoy_i_konstrukt', подпись: 'Отдел разработки технической и конструкторской документации' },
  { код: 'otdel_remonta_dv', подпись: 'Отдел Ремонта ДВ' },
  { код: 'otdel_rybopromyslovogo_oborudovaniya', подпись: 'Отдел рыбопромыслового оборудования' },
  { код: 'otdel_servisa_elektrooborudovaniya_i_avto', подпись: 'Отдел сервиса электрооборудования и автоматики' },
  { код: 'otdel_servisa_radionavigacionnogo_oborudo', подпись: 'Отдел сервиса радионавигационного оборудования' },
  { код: 'otdel_sklad', подпись: 'Отдел склад' },
  { код: 'otdel_vnedreniya_b24', подпись: 'Отдел внедрения Б24' },
  { код: 'otdel_web_razrabotki', подпись: 'Отдел WEB-разработки' },
  { код: 'otdel_zakupki', подпись: 'Отдел закупки' },
  { код: 'podrazdelenie_murmansk', подпись: 'Подразделение Мурманск' },
  { код: 'servis_dv', подпись: 'Сервис ДВ' },
  { код: 'sklad_dv', подпись: 'Склад ДВ' },
  { код: 'spb_servisnaya_gruppa_avtomatika_i_elektr', подпись: 'СПБ Сервисная группа Автоматика и Электрика' },
  { код: 'svarochnyy_uchastok_rnd', подпись: 'Сварочный участок РнД' },
  { код: 'svyaz_i_radionavigaciya', подпись: 'Связь и Радионавигация' },
  { код: 'tehpodderzhka_dv', подпись: 'Техподдержка ДВ' },
  { код: 'tehpodderzhka_ks', подпись: 'Техподдержка КС' },
  { код: 'otdel_it', подпись: 'Отдел ИТ' },
  { код: 'gruppa_remonta', подпись: 'Группа ремонта' },
  { код: 'op_sudostroy_sudoremont', подпись: 'ОП судострой/судоремонт' },
  { код: 'otdel_avtomatiki_i_elektriki', подпись: 'Отдел автоматики и электрики' },
  { код: 'otdel_servisa_murmansk', подпись: 'Отдел сервиса Мурманск' },
  { код: 'sklad_rnd', подпись: 'Склад РнД' },
  { код: 'avtomatika_temryuk', подпись: 'Автоматика Темрюк' },
  { код: 'montazhnaya_gruppa_murmansk', подпись: 'Монтажная группа Мурманск' },
  { код: 'vybyvshie', подпись: 'Выбывшие' },
  { код: 'otdel_servisa_temryuk', подпись: 'Отдел сервиса Темрюк' },
  { код: 'proektnyy_otdel', подпись: 'Проектный отдел' },
  { код: 'departament_it_i_marketinga', подпись: 'Департамент IT и маркетинга' },
  { код: 'ispytatelnyy_srok_rnd', подпись: 'Испытательный срок РнД' },
  { код: 'novaya_buhgalteriya', подпись: 'Новая бухгалтерия' },
  { код: 'obosoblennoe_podrazdelenie_v_g_kaliningra', подпись: 'Обособленное подразделение в г. Калининград' },
  { код: 'otdel_proektnyh_prodazh', подпись: 'Отдел проектных продаж' },
  { код: 'sklad_murmansk', подпись: 'Склад Мурманск' },
  { код: 'yuridicheskaya_sluzhba', подпись: 'Юридическая служба' },
  { код: 'kompleksnoe_upravlenie_logistikoy', подпись: 'Комплексное управление логистикой' },
  { код: 'otdel_aho', подпись: 'Отдел АХО' },
  { код: 'otdel_prodazh_sudovoy_mehaniki', подпись: 'Отдел продаж судовой механики' },
  { код: 'podrazdelenie_dv', подпись: 'Подразделение ДВ' },
  { код: 'polnomochnoe_predstavitelstvo_2', подпись: 'Полномочное представительство 2' },
  { код: 'departament_razrabotki', подпись: 'Департамент разработки' },
  { код: 'otdel_marketinga', подпись: 'Отдел Маркетинга' },
  { код: 'otdel_razrabotki_navigacionnogo_oborudova', подпись: 'Отдел разработки навигационного оборудования' },
  { код: 'podrazdelenie_rostov_na_donu', подпись: 'Подразделение Ростов-на-Дону' },
  { код: 'otdel_ii', подпись: 'Отдел ИИ' },
  { код: 'otdel_razrabotki_sistem_avtomatiki', подпись: 'Отдел разработки систем автоматики' },
  { код: 'zamestitel_direktora_po_obschim_voprosam', подпись: 'Заместитель директора по общим вопросам' },
  { код: 'kommercheskaya_sluzhba', подпись: 'Коммерческая служба' },
  { код: 'polnomochnoe_predstavitelstvo_3', подпись: 'Полномочное представительство 3' },
  { код: 'zamestitel_direktora_po_rnd', подпись: 'Заместитель Директора по RND' },
  { код: 'servisnaya_sluzhba', подпись: 'Сервисная служба' },
  { код: 'sluzhba_glavnogo_inzhenera', подпись: 'Служба главного инженера' },
]

/** Роли — справочник `role`, на него смотрит `employee_post.role_id`. */
export const РОЛИ: Вариант[] = [
  { код: 'trainee', подпись: 'Стажёр' },
  { код: 'assistant', подпись: 'Ассистент' },
  { код: 'head', подпись: 'Руководитель' },
  { код: 'coordinator', подпись: 'Координатор' },
  { код: 'accountant', подпись: 'Бухгалтер' },
  { код: 'lawyer', подпись: 'Юрист' },
  { код: 'hr', подпись: 'Кадры' },
  { код: 'marketing', подпись: 'Маркетинг' },
  { код: 'engineer', подпись: 'Инженер' },
  { код: 'logistics', подпись: 'Логистика и склад' },
  { код: 'manager', подпись: 'Менеджер по продажам' },
  { код: 'specialist', подпись: 'Специалист' },
  { код: 'unknown', подпись: 'Должность не указана' },
  { код: 'other', подпись: 'Прочее' },
]

// ─────────────────────────── документы ───────────────────────────

/** Виды договора — справочник `contract_kind`. Единственный список, который сошёлся сразу. */
export const ВИДЫ_ДОГОВОРА: Вариант[] = [
  { код: 'frame', подпись: 'Рамочный' },
  { код: 'single', подпись: 'Разовый' },
  { код: 'annex', подпись: 'Приложение к рамочному' },
]

/**
 * Направление счёта. Колонка `invoice.direction` текстовая, справочника под ней нет —
 * значит этот список и есть правило, и проверяет его слой записи по нему же.
 */
export const НАПРАВЛЕНИЯ_СЧЁТА: Вариант[] = [
  { код: 'out', подпись: 'Мы выставили', подсказка: 'Счёт заказчику: ждём поступления' },
  { код: 'in', подпись: 'Нам выставили', подсказка: 'Счёт поставщика: нужна статья расходов' },
]

/** Статьи расходов — справочник `expense_item`. */
export const СТАТЬИ_РАСХОДОВ: Вариант[] = [
  { код: 'deal', подпись: 'Под сделку' },
  { код: 'stock_goods', подпись: 'Товар на склад' },
  { код: 'office', подпись: 'Офис' },
  { код: 'other', подпись: 'Прочие' },
  { код: 'rnd_production', подпись: 'Разработка и производство' },
  { код: 'auto', подпись: 'Авто' },
  { код: 'transport', подпись: 'Транспортные' },
  { код: 'office_equipment', подпись: 'Офисная техника' },
  { код: 'tenders', подпись: 'Участие в тендерах' },
  { код: 'marketing', подпись: 'Маркетинг' },
  { код: 'exhibitions', подпись: 'Выставки' },
  { код: 'capex', подпись: 'Инвестиционные расходы' },
  { код: 'travel', подпись: 'Командировки' },
  { код: 'academy', подпись: 'Академия' },
]

/** Виды проверок — справочник `inspection_kind`: приборы, по которым идут сроки. */
export const ВИДЫ_ПРОВЕРКИ: Вариант[] = [
  { код: 'sbm_epirb', подпись: 'Береговое ТО АРБ (БТО)' },
  { код: 'gmdss_6322', подпись: 'Ежегодная проверка ГМССБ, ф. 6.3.22.2' },
  { код: 'epirb', подпись: 'Ежегодная проверка АРБ' },
  { код: 'vhf', подпись: 'Ежегодная проверка УКВ радиостанции ГМССБ' },
  { код: 'sart', подпись: 'Ежегодная проверка РЛО' },
  { код: 'ais', подпись: 'Ежегодная проверка АИС' },
  { код: 'batt', подпись: 'Ежегодная проверка АКБ ГМССБ' },
  { код: 'ais_sart', подпись: 'Проверка АИС-РЛО' },
]

/**
 * Результат проверки. Колонка `inspection.result` текстовая, справочника под ней нет —
 * список из описания и есть правило.
 */
export const РЕЗУЛЬТАТЫ_ПРОВЕРКИ: Вариант[] = [
  { код: 'ok', подпись: 'Исправно' },
  { код: 'defect', подпись: 'Есть замечания', подсказка: 'Замечания перечисляются в заключении: без них акт не примут' },
  { код: 'not_done', подпись: 'Не выполнена', подсказка: 'Прибор недоступен, судно в рейсе' },
]

// ─────────────────────────── состояние документа в значениях ───────────────────────────

/**
 * Служебный ключ состояния документа ВНУТРИ значений.
 *
 * ЗАЧЕМ ОН ЕСТЬ. Обязательность номера у договора, счёта и акта условная: номер выдаётся
 * при переходе `draft → issued` и только там, у черновика номера нет вовсе. Но функция
 * обязательности видит одни значения — состояние записи лежит рядом, в
 * `ЗаписьКарточки.состояние`. Поэтому источник обязан класть состояние ещё и в значения,
 * под этим ключом; поддельный источник (`fake.ts`) так и делает, и того же ждём от BFF.
 *
 * ЧТО СЛОМАЕТСЯ БЕЗ НЕГО. Номер придётся сделать обязательным всегда — и завести черновик
 * станет нельзя, потому что номера у него ещё нет и взять его неоткуда.
 */
export const КЛЮЧ_СОСТОЯНИЯ = 'doc_state'

/** Документ выпущен: номер уже выдан и обязан быть на месте. */
export const выпущен = (з: Значения): boolean => строка(з, КЛЮЧ_СОСТОЯНИЯ) === 'issued'

// ─────────────────────────── мелкая помощь показателям ───────────────────────────

/** Значение показателя или прочерк: пустая плитка в шапке читается как поломка. */
export const илиПрочерк = (s: string): string => (s.trim() === '' ? '—' : s)

/** Подпись варианта по коду из значений. */
export function подписьВарианта(варианты: Вариант[], з: Значения, код: string): string {
  const v = з[код]
  if (пусто(v)) return '—'
  const s = String(v)
  return варианты.find((x) => x.код === s)?.подпись ?? s
}
